import { useForm as createUseForm } from "@ant-design-vue/use";
import { Col, Row } from "ant-design-vue";
import lodash from "lodash";
import { defineAsyncComponent, reactive, toRaw } from "vue";
import { addModal, setIsLoading as setIsLoadingApp } from "../../../../../app.logic";
import { setAppPageHeader } from "../../../../../components/layout/app-layout/app-layout.logic";
import i18n from "../../../../../i18n";
import store from "../../../../../store";
import {
    createButtonActive,
    createButtonArchive,
    createButtonCreate,
    createButtonDelete,
    createButtonEdit,
    createButtonSave,
    createButtonView
} from "../../../../../utils/button";
import {
    checkAttribute,
    initUseForm,
    onClickActive,
    onClickArchive,
    onClickCreate,
    onClickDelete,
    onClickEdit,
    onClickView
} from "../../../../../utils/form";
import { createLoading } from "../../../../../utils/loading";
import { has } from "../../../../../utils/menu-context";
import {
    createAppPageHeaderTop,
    createManageExportCsv,
    createManageSelect,
    createPage, createRowActionOpenUrl,
    createRowActionPdf
} from "../../../../../utils/page";
import { flatWhereGroup } from "../../../../../utils/search";
import { validateError } from "../../../../../utils/validate";
import { createConditionAction, createConditionForm } from "./sequestrate.condition";
import { createColumns, createField } from "./sequestrate.section";
import { modalStyleIFrame } from "../../../../../utils/constant";

const CIcon = defineAsyncComponent(() => import("../../../../../components/icon/CIcon"));
const CIFrame = defineAsyncComponent(() => import("../../../../../components/iframe/CIFrame"));

export const { isLoading, setIsLoading } = createLoading();

export const actionPrefix = "resource/v1/transaction/sequestrate";

export const callSave = ({ form }) => store.dispatch(`${actionPrefix}/save`, { data: form });
export const callSearch = ({ criteria }) => store.dispatch(`${actionPrefix}/search`, { criteria });
export const callActive = ({ id }) => store.dispatch(`${actionPrefix}/active`, { id });
export const callArchive = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id });
export const callDelete = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id, config: { params: { isHardDelete: true } } });
export const callActiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/activeAll`, { ids });
export const callArchiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids });
export const callDeleteAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids, config: { params: { isHardDelete: true } } });
export const callChanges = ({ id, criteria }) => store.dispatch(`${actionPrefix}/changes`, { id, criteria });
export const callReportData = ({ data }) => store.dispatch(`${actionPrefix}/reportData`, { data });

export const onSearch = ({ props, page }) => {
    const where = page.where || [];
    const sort = page.sort || [];
    const customWhere = lodash.cloneDeep(where);
    const companyNameThIndex = customWhere.findIndex(d => d.name === "refUserCompany.companyNameTh");
    if (companyNameThIndex !== -1) {
        const companyNameThWhere = customWhere[companyNameThIndex];
        customWhere.splice(
            companyNameThIndex,
            1,
            {
                openBucket: true,
                name: "refUserCompany.companyNameTh",
                type: "String",
                operator: "like",
                value: companyNameThWhere.value
            },
            {
                logic: "OR",
                closeBucket: true,
                name: "refUserCompany.fullNameTh",
                type: "String",
                operator: "like",
                value: companyNameThWhere.value
            }
        );
    }
    setIsLoading(true);
    window.onSearch?.({ page })
    return callSearch({ criteria: { ...page, data: [], where: [...customWhere, ...createWhereFor({ props })], sort } })
        .then(page => (state.page = { ...page, where, sort }))
        .finally(() => setIsLoading(false));
};

export const onClickSave = ({ props, data, refs, formParams }) => {
    const { viewOnly, routerPrefix } = props;
    return data.useForm
        .validate()
        .then(() => {
            setIsLoadingApp(true);
            return callSave({ form: { ...data.useForm.modelRef, ...formParams } })
                .finally(() => setIsLoadingApp(false))
                .then(record => {
                    if (viewOnly) initForm({ props, data, refs });
                    else onClickView({ record, routerPrefix });
                });
        })
        .catch(error => {
            validateError({ error });
            throw error;
        });
};

export const onClickModalOk = ({ data }) => {
    return data.useForm
        .validate()
        .then(() => ({ ...toRaw(data.useForm.modelRef), isSave: true }))
        .catch(error => {
            validateError({ error });
            throw error;
        });
};

export const openModalList = ({ bind, onIframeMounted }) => {
    const options = addModal({
        bind: lodash.merge(lodash.cloneDeep(modalStyleIFrame), bind),
        render: () => {
            return <CIFrame
                src={`${process.env.VUE_APP_PUBLIC_PATH}/app/release/document/sequestrates?isLayout=false&canSelect=true&cacheQuery=true`}
                onIframeMounted={({ refs }) => onIframeMounted({ options, refs })}
            ></CIFrame>
        }
    });
    return options;
};

export const createSearchOneCriteria = ({ id, forLog }) => {
    if (!forLog) {
        return {
            select: ["**"],
            page: 0,
            size: 1,
            where: [{ name: "id", type: "UUID", operator: "=", value: id }],
            children: []
        };
    } else {
        return {
            select: [],
            page: 0,
            size: 1,
            where: [{ name: "id", type: "UUID", operator: "=", value: id }],
            children: []
        };
    }
};

export const createWhereFor = ({ props }) => {
    const { menuContext } = props;
    const whereGroup = [];
    const userInfo = store.state.resource.auth.info?.user;
    if (has({ menuContext, key: "forRequest" })) {
        const where = [{ name: "refUserCompany.id", type: "UUID", operator: "=", value: userInfo.id }];
        if (userInfo.refParent?.id) where.push({ logic: "OR", name: "refUserCompany.id", type: "UUID", operator: "=", value: userInfo.refParent.id });
        whereGroup.push(where);
    }
    if (has({ menuContext, key: "canViewAll" })) {
        whereGroup.push([{ constant: true, name: "1", type: "String", operator: "=", value: "1" }]);
    }
    return [
        { name: "isTemp", type: "Boolean", operator: "=", value: false },
        ...flatWhereGroup({ where: whereGroup, logic: "OR" })
    ];
};

export const createForm = () => {
    return {
        refDepartment: null,
        refUserCompany: null,
        refUser: null,
        refRequestDoc: null,
        model: null,
        refModel: null,
        refInformTemp: null,
        refStatus: null,
        sequestrateDatetime: null,
        transportDate: null,
        refInspector: null,
        inspectorTel: null,
        containerNumber: null,
        vehicleNumber: null,
        isUnlock: null,
        unlockRemark: null,
        isRiskValidated: null,
        isInspectRiskValidated: null,
        isInspect: null,
        inspectRemark: null,
        refInspectLocation: null,
        refInspectDepartment: null,
        refStorageLocation: null,
        locationRemark: null,
        refResultDepartment: null,
        refSequestrate: null,
        isSampling: null,
        refContactStaffType: null,
        abnormalRemark: null,
        officerRemark: null,
        isTemp: null,
        officers: [],
        products: [],
        attachments: [],
        statuses: []
    };
};

export const createRules = () => {
    return {};
};

export const createFormTitle = ({ data }) => {
    const modelRef = data.useForm.modelRef;
    return modelRef.id ? `${modelRef.name} (${modelRef.refStatus?.value})` : i18n.global.t("button.create");
};

export const loadForm = ({ props, data, refs }) => {
    setIsLoading(true);
    const { form } = props;
    const { id, useForm } = data;
    const { modelRef } = useForm;
    let promise = null;
    if (form) {
        promise = Promise.resolve({ data: [form] });
    } else if (id) {
        promise = callSearch({ criteria: createSearchOneCriteria({ id }) });
    } else {
        promise = Promise.resolve({ data: [useForm.modelRef] });
    }
    return promise
        .then(page => initUseForm({ props, data, refs, page }))
        .then(() => loadDefaultForm({ props, data, refs }))
        .then(() => initUseForm({ props, data, refs, page: { data: [modelRef] } }))
        .finally(() => setIsLoading(false));
};

export const loadDefaultForm = () => {
    const promises = [];
    return Promise.all(promises);
};

export const initState = ({ props }) => {
    const { menuContext } = props;
    state.manages = [
        createManageSelect({ menuContext }),
        createManageExportCsv({ menuContext })
    ];
    state.manageExtras = [];
    state.rowActions = [
        /*
        createRowActionView({ menuContext, onClick: ({ record }) => onClickView({ record, routerPrefix }) }),
        */
        createRowActionPdf({
            visible: () => false,
            bind: { class: "btn-primary", size: "small", icon: () => <CIcon file="search.svg"></CIcon> },
            text: () => i18n.global.t("button.viewDocument"),
            callReport: ({ record }) => callReportData({ data: { id: record.id } }),
            fileName: "report.pdf"
        }),
        createRowActionOpenUrl({
            bind: { class: "btn-primary", size: "small", icon: () => <CIcon file="search.svg"></CIcon> },
            text: () => i18n.global.t("button.viewDocument"),
            callUrl: ({ record }) => `${process.env.VUE_APP_API_REPORT_URL}/files/haz/dof/${record.id}/file-result.pdf?contentDisposition=inline&_timestamp=${new Date().getTime()}`
        })
    ];
};

export const initForm = ({ props, data, refs }) => {
    const { routerPrefix } = props;
    checkAttribute({ props, data, refs });
    data.form = createForm({ props, data, refs });
    data.rules = createRules({ props, data, refs });
    data.useForm = createUseForm(data.form, data.rules);
    data.fields = createField({ props, data, refs, isLoading });
    data.conditionForms = createConditionForm({ props, data, refs, isLoading });
    loadForm({ props, data, refs }).then(() => {
        if (props.setAppPageHeader) {
            setAppPageHeader({
                slots: {
                    top: () => createAppPageHeaderTop({ props, data, refs, state, routerPrefix, onClickView, onClickEdit }),
                    default: () => createAppPageHeaderDefault({ props, data, refs })
                }
            });
        }
    });
};

export const createAppPageHeaderDefault = ({ props, data, refs }) => {
    const col1 = [];
    const col2 = [];
    const { menuContext, viewOnly, routerPrefix } = props;
    const { useForm } = data;
    const { modelRef, initialModel } = useForm;
    const { id, isActive } = modelRef;
    const canEdit = state.conditionAction.canEdit({ props, record: initialModel });
    if (id && has({ menuContext, key: "canCreate" })) {
        col1.push(createButtonCreate({ onClick: () => onClickCreate({ routerPrefix }), classStyle: "m-1" }));
    }
    if (!viewOnly && id && has({ menuContext, key: "canView" })) {
        col1.push(createButtonView({ onClick: () => onClickView({ record: { id }, routerPrefix }), classStyle: "m-1" }));
    }
    if (viewOnly && id && isActive && canEdit) {
        col1.push(createButtonEdit({ onClick: () => onClickEdit({ record: { id }, routerPrefix }), classStyle: "m-1" }));
    }
    if (!viewOnly && (!id || (id && isActive && canEdit))) {
        col1.push(createButtonSave({ onClick: () => onClickSave({ props, data, refs }), classStyle: "m-1" }));
    }
    if (viewOnly && id && !isActive && has({ menuContext, key: "canActive" })) {
        col2.push(
            createButtonActive({
                onClick: () =>
                    onClickActive({
                        record: { id },
                        callActive,
                        setIsLoading: setIsLoadingApp,
                        onClickEdit: () => onClickEdit({ record: { id }, routerPrefix })
                    }),
                classStyle: "m-1"
            })
        );
    }
    if (!viewOnly && id && isActive && has({ menuContext, key: "canArchive" })) {
        col2.push(
            createButtonArchive({
                onClick: () =>
                    onClickArchive({
                        record: { id },
                        callArchive,
                        setIsLoading: setIsLoadingApp,
                        onClickView: () => onClickView({ record: { id }, routerPrefix })
                    }),
                classStyle: "m-1"
            })
        );
    }
    if (!viewOnly && id && isActive && has({ menuContext, key: "canDelete" })) {
        col2.push(
            createButtonDelete({
                onClick: () => onClickDelete({ record: { id }, callDelete, setIsLoading: setIsLoadingApp, state, routerPrefix }),
                classStyle: "m-1"
            })
        );
    }
    return (
        <Row type="flex">
            <Col>{col1}</Col>
            <Col class="ml-auto">{col2}</Col>
        </Row>
    );
};

export const state = reactive({
    columns: createColumns(),
    page: createPage({
        options: {
            select: [
                "*",
                "refDepartment.*",
                "refUserCompany.*",
                "refRequestType.*",
                "refStatus.*",
                "refUserCompany.*",
                "refUserCompany.refPersonType.*",
                "refReleaseProduct.id",
                "refReleaseProduct.refRelease.id",
                "refReleaseProduct.refRelease.refInspection.id",
                "refReleaseProduct.refRelease.refInspection.refRequestDoc.*",
                "refReleaseProduct.refRelease.refInspection.refInformTemp.id",
                "refReleaseProduct.refRelease.refInspection.refInformTemp.approveCode",
                "refReleaseProduct.refInspectionProduct.id",
                "refReleaseProduct.refInspectionProduct.refInformProduct.*",
            ],
            sort: [{ name: "updateDate", type: "DESC" }],
        }
    }),
    tableOptions: { search: { activeKey: "Search" } },
    conditionAction: createConditionAction()
});
